import {
    BLEND_NORMAL,
    CULLFACE_NONE,
    Entity,
    Mesh,
    MeshInstance,
    PlaneGeometry,
    Quat,
    RotateGizmo,
    StandardMaterial,
    TranslateGizmo,
    Vec2,
    Vec3,
    type TransformGizmo
} from 'playcanvas';

import { configureAxisOnlyRotateGizmo } from './rotate-gizmo-config';
import type { Scene } from '../scene';

const tmpV = new Vec3();
const tmpQ = new Quat();
const tmpUp = new Vec3();

export type MapPlotPlaneGizmoMode = 'translate' | 'rotate';

/**
 * Ground plane for “floor” alignment: bright plane mesh + SuperSplat-style move/rotate gizmos.
 */
export class MapPlotGroundPlaneGizmo {
    private readonly scene: Scene;
    private readonly entity: Entity;
    private readonly material: StandardMaterial;
    private readonly mesh: Mesh;
    private readonly translateGizmo: TranslateGizmo;
    private readonly rotateGizmo: RotateGizmo;
    private readonly updateGizmoSize: () => void;

    readonly normal = new Vec3(0, 1, 0);
    readonly center = new Vec3(0, 0, 0);
    private halfSize = 1;
    private planeGizmoMode: MapPlotPlaneGizmoMode = 'translate';
    private transformGizmosActive = false;

    constructor(scene: Scene) {
        this.scene = scene;
        const app = scene.app;
        const device = app.graphicsDevice;
        this.halfSize = Math.max(scene.camera?.sceneRadius ?? 1, 1) * 0.55;
        const geom = new PlaneGeometry({
            halfExtents: new Vec2(this.halfSize, this.halfSize),
            widthSegments: 1,
            lengthSegments: 1
        });
        this.mesh = Mesh.fromGeometry(device, geom);
        this.material = new StandardMaterial();
        this.material.cull = CULLFACE_NONE;
        /* Bright “sheet” visible over splat — white with mild glow */
        this.material.diffuse.set(1, 1, 1);
        this.material.emissive.set(0.22, 0.24, 0.28);
        this.material.emissiveIntensity = 1;
        this.material.opacity = 0.68;
        this.material.blendType = BLEND_NORMAL;
        /* Same depth buffer as splat pass — plane occludes splat behind it (intersection read). */
        this.material.depthWrite = true;
        this.material.depthTest = true;
        this.material.useLighting = false;
        this.material.update();

        const meshInstance = new MeshInstance(this.mesh, this.material);
        this.entity = new Entity('volugraphMapPlotGroundPlane', app);
        this.entity.addComponent('render', {
            meshInstances: [meshInstance],
            layers: [scene.worldLayer.id]
        });
        scene.contentRoot.addChild(this.entity);

        const fp = scene.camera.focalPointTween?.value;
        if (fp) {
            this.center.copy(fp);
        }
        this.syncEntityPose();

        this.translateGizmo = new TranslateGizmo(scene.camera.camera, scene.gizmoLayer);
        this.translateGizmo.coordSpace = 'world';
        this.rotateGizmo = new RotateGizmo(scene.camera.camera, scene.gizmoLayer);
        this.rotateGizmo.rotationMode = 'orbit';
        configureAxisOnlyRotateGizmo(this.rotateGizmo);

        const wire = (gizmo: TransformGizmo) => {
            gizmo.on('render:update', () => {
                this.scene.forceRender = true;
            });
            gizmo.on('transform:move', () => {
                this.pullPoseFromEntity();
                this.scene.forceRender = true;
            });
        };
        wire(this.translateGizmo);
        wire(this.rotateGizmo);

        this.updateGizmoSize = () => {
            const { camera, canvas } = scene;
            const size = camera.ortho ?
                1125 / canvas.clientHeight :
                1200 / Math.max(canvas.clientWidth, canvas.clientHeight);
            this.translateGizmo.size = size;
            this.rotateGizmo.size = size;
        };
        this.updateGizmoSize();
        scene.events.on('camera.resize', this.updateGizmoSize);
        scene.events.on('camera.ortho', this.updateGizmoSize);
    }

    private syncEntityPose(): void {
        this.entity.setPosition(this.center);
        tmpV.copy(Vec3.UP);
        tmpQ.setFromDirections(tmpV, this.normal);
        this.entity.setRotation(tmpQ);
    }

    private pullPoseFromEntity(): void {
        this.center.copy(this.entity.getPosition());
        tmpUp.copy(Vec3.UP);
        this.entity.getRotation().transformVector(tmpUp, this.normal);
    }

    setCenter(x: number, y: number, z: number): void {
        this.center.set(x, y, z);
        this.syncEntityPose();
        this.scene.forceRender = true;
    }

    /** Set world-space unit normal (up of the ground plane). */
    setNormal(nx: number, ny: number, nz: number): void {
        const len = Math.hypot(nx, ny, nz) || 1;
        this.normal.set(nx / len, ny / len, nz / len);
        this.syncEntityPose();
        this.scene.forceRender = true;
    }

    setPlaneGizmoMode(mode: MapPlotPlaneGizmoMode): void {
        this.planeGizmoMode = mode;
        if (!this.transformGizmosActive) {
            return;
        }
        this.translateGizmo.detach();
        this.rotateGizmo.detach();
        this.translateGizmo.enabled = mode === 'translate';
        this.rotateGizmo.enabled = mode === 'rotate';
        if (mode === 'translate') {
            this.translateGizmo.attach([this.entity]);
        } else {
            this.rotateGizmo.attach([this.entity]);
        }
    }

    /** Show PlayCanvas transform gizmos (call when map-plot plane step is active). */
    activatePlaneGizmos(initialMode: MapPlotPlaneGizmoMode = 'translate'): void {
        this.translateGizmo.enabled = true;
        this.rotateGizmo.enabled = true;
        this.transformGizmosActive = true;
        this.setPlaneGizmoMode(initialMode);
    }

    deactivatePlaneGizmos(): void {
        this.transformGizmosActive = false;
        this.translateGizmo.detach();
        this.rotateGizmo.detach();
        this.translateGizmo.enabled = false;
        this.rotateGizmo.enabled = false;
    }

    getPlaneGizmoMode(): MapPlotPlaneGizmoMode {
        return this.planeGizmoMode;
    }

    getGroundPlane(): { normal: [number, number, number]; pointOnPlane: [number, number, number] } {
        this.pullPoseFromEntity();
        return {
            normal: [this.normal.x, this.normal.y, this.normal.z],
            pointOnPlane: [this.center.x, this.center.y, this.center.z]
        };
    }

    destroy(): void {
        this.deactivatePlaneGizmos();
        this.scene.events.off('camera.resize', this.updateGizmoSize);
        this.scene.events.off('camera.ortho', this.updateGizmoSize);
        try {
            this.translateGizmo.destroy();
        } catch {
            /* ignore */
        }
        try {
            this.rotateGizmo.destroy();
        } catch {
            /* ignore */
        }
        try {
            this.entity.destroy();
        } catch {
            /* ignore */
        }
        try {
            this.mesh.destroy();
        } catch {
            /* ignore */
        }
        try {
            this.material.destroy();
        } catch {
            /* ignore */
        }
    }
}
